import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { usePageTitle } from './hooks/usePageTitle';

function newslettersSanitizeName(name: string): [string, boolean] {
  if (name == '') return ['', false];
  let sanitized_name: string = name.trim();
  return [sanitized_name, true];
}

// https://regexr.com/3e48o
const newslettersEmailPattern: RegExp = new RegExp(
  '^[\\w\\-.]+@([\\w-]+\\.)+[\\w-]{2,4}$'
);
function newslettersSanitizeEmail(email: string): [string, boolean] {
  if (email == '') return ['', false];
  let sanitized_email: string = email.trim();
  let match = sanitized_email.match(newslettersEmailPattern);
  if (!match) return [sanitized_email, false];
  return [sanitized_email, true];
}

function Newsletters() {
  usePageTitle('Newsletters')
  const [error, setError] = useState<string | null>(null);
  const [loadingSubscribe, setLoadingSubscribe] = useState<boolean | null>(
    null
  );
  const [loadingUnsubscribe, setLoadingUnsubscribe] = useState<boolean | null>(
    null
  );
  const [showSuccessSubscribe, setShowSuccessSubscribe] = useState(false);
  const [showSuccessUnsubscribe, setShowSuccessUnsubscribe] = useState(false);

  const successTimerRef = useRef<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, []);

  async function handleSubmitSubscribe(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // set logic component states
    setError(null);
    setLoadingSubscribe(true);
    setShowSuccessSubscribe(false);
    const form = e.currentTarget as HTMLFormElement;
    const form_data = new FormData(form);

    const name = String(form_data.get('name') || '');
    const email = String(form_data.get('email') || '');
    // honeypot field
    const honey = String(form_data.get('email2') || '');

    // typed DOM lookup to avoid `any`/null issues
    const agreeElem = document.querySelector<HTMLInputElement>(
      '#ts-newsletter-form-agree'
    );
    const agree_box: boolean = agreeElem ? agreeElem.checked : false;
    if (agree_box == false) {
      setLoadingSubscribe(false);
      setError(
        `You must agree to the terms and conditions to be added to the mailing list.`
      );
      return;
    }

    // sanitize
    const [fmt_name, ok_name] = newslettersSanitizeName(name);
    const [fmt_email, ok_email] = newslettersSanitizeEmail(email);
    if (!ok_name || !ok_email) {
      setError('Invalid name or email');
      setLoadingSubscribe(false);
      return;
    }

    try {
      const res = await fetch('/api/newsletters/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: fmt_name,
          email: fmt_email,
          tacagree: agree_box,
          honey: honey
        })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message || `Subscribe failed (${res.status})`);
        setLoadingSubscribe(false);
        return;
      }

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      setShowSuccessSubscribe(true);
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccessSubscribe(false);
        setLoadingSubscribe(false);
        successTimerRef.current = null;
      }, 3000);
    } catch (error) {
      setError('Network error - please try again.');
      setLoadingSubscribe(false);
    }
  }

  async function handleSubmitUnsubscribe(
    e?: React.MouseEvent<HTMLButtonElement>
  ) {
    if (e) e.preventDefault();
    setError(null);
    setLoadingUnsubscribe(true);
    setShowSuccessUnsubscribe(false);

    if (!formRef.current) {
      setError('Form not found');
      setLoadingUnsubscribe(false);
      return;
    }

    const form_data = new FormData(formRef.current);
    const email = String(form_data.get('email') || '');

    // sanitize and validate
    const [fmt_email, ok_email] = newslettersSanitizeEmail(email);
    if (!ok_email) {
      setError('Invalid email');
      setLoadingUnsubscribe(false);
      return;
    }

    try {
      const res = await fetch('/api/newsletters/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: fmt_email })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message || `Unsubscribe failed (${res.status})`);
        setLoadingUnsubscribe(false);
        return;
      }
      setShowSuccessUnsubscribe(true);
      setLoadingUnsubscribe(false);
      setError(null);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccessUnsubscribe(false);
        setLoadingUnsubscribe(false);
        successTimerRef.current = null;
      }, 3000);
    } catch (err) {
      setError('Network error - please try again.');
      setLoadingUnsubscribe(false);
    }
  }

  return (
    <div
      id="ts-newsletter-wrapper"
      className="w-full h-full flex items-center justify-center"
    >
      <div
        id="ts-newsletter-content"
        className="xl:w-2/3 w-full p-4 h-full flex flex-col items-center"
      >
        <div className="w-full relative flex items-center justify-center text-2xl p-2 border-b border-light-border">
          Newsletters
        </div>
        <div className="w-full flex flex-col items-center text-normal p-2">
          <span>
            TerraNotes is the newsletter that we publish occasionally with news
            about TerraSciences and tips on how to use TerraStation.
          </span>
          <div className="p-2"></div>
          <span>
            You can download TerraNotes{' '}
            <a
              className="text-light-text-link"
              href="/client-center/download-terranotes"
            >
              here
            </a>
            .
          </span>
          <div className="p-2"></div>
          <span>
            If you wish to be notified whenever a new edition of TerraNotes is
            published, please enter your details here and click Subscribe.
          </span>
          <form
            ref={formRef}
            name="tsnewsletterform"
            method="POST"
            id="ts-newsletterform"
            className="w-4/5 xl:w-1/3 h-full p-4 relative"
            onSubmit={handleSubmitSubscribe}
          >
            {showSuccessSubscribe && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-30 pointer-events-none">
                <div className="bg-none text-light-text-alt px-6 py-3">
                  Subscribed
                </div>
              </div>
            )}
            {showSuccessUnsubscribe && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-30 pointer-events-none">
                <div className="bg-none text-light-text-alt px-6 py-3">
                  Unsubscribed
                </div>
              </div>
            )}
            <div
              id="ts-newsletter-form-group-name"
              className="w-full flex flex-col items-center justify-center mt-2"
            >
              <label htmlFor="ts-newsletter-form-name">Name</label>
              <input
                className="w-full p-2 border-light-border border focus:border-light-main"
                type="text"
                id="ts-newsletter-form-name"
                name="name"
                placeholder=""
                aria-label="Name"
                required
              />
            </div>
            <div
              id="ts-newsletter-form-group-email"
              className="w-full flex flex-col items-center justify-center mt-2"
            >
              <label htmlFor="ts-newsletter-form-name">Email</label>
              <input
                className="w-full p-2 border-light-border border focus:border-light-main"
                type="email"
                id="ts-newsletter-form-email"
                name="email"
                placeholder=""
                aria-label="Email"
                required
              />
            </div>
            <div
              id="ts-newsletter-form-group-tacpriv"
              className="w-full flex flex-row items-center justify-center mt-4 space-x-4"
            >
              <input
                type="checkbox"
                id="ts-newsletter-form-agree"
                name="agree"
                aria-label="Agree to Terms and Conditions and the Privacy Policy"
              />
              <span>
                I agree with the{' '}
                <a href="../privacy/terms-of-use">
                  {' '}
                  <span className="text-light-text-link">
                    Terms of Use
                  </span>{' '}
                </a>
                <a href="../privacy/privacy-policy">
                  and the{' '}
                  <span className="text-light-text-link">Privacy Policy</span>.
                </a>
              </span>
            </div>
            <div className="w-full items-center hidden justify-center p-2">
              <input
                className="hidden"
                type="text"
                name="email2"
                placeholder=""
              />
            </div>

            <div
              id="ts-newsletter-form-group-buttons"
              className="w-full flex flex-row items-center justify-center mt-4 space-x-2"
            >
              <button
                id="ts-newsletter-form-subscribe"
                className="w-fit rounded-sm p-2 cursor-pointer bg-light-main hover:bg-light-main-hover duration-200 ease-in-out text-light-text-alt disabled:opacity-60"
                name="subscribe"
                type="submit"
                disabled={loadingSubscribe === true}
              >
                Subscribe
              </button>
              <button
                id="ts-newsletter-form-unsubscribe"
                className="w-fit rounded-sm p-2 cursor-pointer border border-light-border duration-200 ease-in-out text-light-text disabled:opacity-60"
                type="button"
                onClick={handleSubmitUnsubscribe}
                disabled={loadingUnsubscribe === true}
              >
                Unsubscribe
              </button>
            </div>
            <div
              className="flex items-center justify-center p-2 w-full"
              id="ts-newsletter-error"
            >
              {error && <div className="text-sm text-red-500">{error}</div>}
            </div>
          </form>
          <div className="text-normal text-center text-light-text w-full flex ">
            <span>
              If you wish to be removed from the TerraNotes mailing list, please
              enter your email address above and click Unsubscribe
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Newsletters;
